'use client';

import { useState, useEffect, use } from 'react';
import Link from 'next/link';
import {
  fetchResultadosPublicos,
  fetchIndicadoresVigentes,
  type ResultadoPublicoApi,
  type IndicadorVigenteApi,
} from '@/services/indicadores';

export default function IndicatorDetailPage({
  params,
}: {
  params: Promise<{ id: string }> | { id: string };
}) {
  const resolvedParams = params instanceof Promise ? use(params) : params;
  const rawId = decodeURIComponent(resolvedParams.id || '');

  const [loading, setLoading] = useState(true);
  const [indicatorMeta, setIndicatorMeta] = useState<IndicadorVigenteApi | null>(null);
  const [historicalData, setHistoricalData] = useState<ResultadoPublicoApi[]>([]);

  useEffect(() => {
    let active = true;
    const loadData = async () => {
      try {
        const [metaByCode, histByCode, allVigentes, allPublicos] = await Promise.all([
          fetchIndicadoresVigentes({ codigo_interno: rawId }).catch(() => []),
          fetchResultadosPublicos({ codigo_interno: rawId }).catch(() => []),
          fetchIndicadoresVigentes().catch(() => []),
          fetchResultadosPublicos().catch(() => []),
        ]);

        if (!active) return;

        const matchedMeta =
          metaByCode[0] ||
          allVigentes.find(
            (m) =>
              m.codigo_interno === rawId ||
              String(m.indicador_id) === rawId ||
              m.indicador.toLowerCase() === rawId.toLowerCase()
          ) ||
          null;

        setIndicatorMeta(matchedMeta);

        const targetCode = matchedMeta?.codigo_interno || rawId;
        const matchedHist =
          histByCode.length > 0
            ? histByCode
            : allPublicos.filter(
                (r) =>
                  r.codigo_interno === targetCode ||
                  String(r.corrida_id) === rawId ||
                  (matchedMeta && r.indicador === matchedMeta.indicador)
              );

        setHistoricalData(
          matchedHist.sort(
            (a, b) =>
              new Date(a.periodo_referencia).getTime() - new Date(b.periodo_referencia).getTime()
          )
        );
      } catch (err) {
        console.error('Failed to fetch indicator detail', err);
      } finally {
        if (active) setLoading(false);
      }
    };
    void loadData();
    return () => {
      active = false;
    };
  }, [rawId]);

  const latestResult =
    historicalData.length > 0 ? historicalData[historicalData.length - 1] : null;

  const displayTitle =
    indicatorMeta?.indicador || latestResult?.indicador || `Indicador ${rawId}`;
  const displayCategory =
    indicatorMeta?.categoria || latestResult?.categoria || 'Sin categoría asignada';
  const displayUnit =
    indicatorMeta?.unidad_medida || latestResult?.unidad_medida || 'Sin unidad';
  const displaySymbol =
    indicatorMeta?.unidad_simbolo || latestResult?.unidad_simbolo || '';
  const displayDescription =
    indicatorMeta?.formula_texto || 'Sin descripción metodológica disponible.';
  const displayFormula =
    indicatorMeta?.formula_expression ||
    indicatorMeta?.formula_texto ||
    'No especificada';
  const displayPeriodicidad = indicatorMeta?.periodicidad || 'No especificada';

  const hasHistData = historicalData.length > 0;
  const chartData = historicalData.map((res, i) => {
    const year = res.periodo_referencia
      ? new Date(res.periodo_referencia).getFullYear()
      : 'S/D';
    const value =
      typeof res.valor_resultado === 'number'
        ? res.valor_resultado
        : parseFloat(String(res.valor_resultado).replace(/[^\d.-]/g, '')) || 0;
    const label =
      typeof res.valor_resultado === 'number'
        ? res.valor_resultado.toLocaleString('es-AR')
        : String(res.valor_resultado);
    const opacities = ['bg-[#89B6FF]', 'bg-[#5B95FF]', 'bg-[#2E74FF]', 'bg-[#0053D6]'];
    const opacity = opacities[i % opacities.length];

    return { year, value, label, opacity };
  });

  const maxValue = chartData.length > 0 ? Math.max(...chartData.map((d) => d.value), 1) : 1;

  const displayLatestValue = latestResult
    ? typeof latestResult.valor_resultado === 'number'
      ? latestResult.valor_resultado.toLocaleString('es-AR')
      : String(latestResult.valor_resultado)
    : 'S/D';

  const displayLatestPeriod = latestResult?.periodo_referencia
    ? new Date(latestResult.periodo_referencia).getFullYear().toString()
    : 'Sin publicar';

  if (loading) {
    return (
      <main className="max-w-6xl mx-auto w-full px-8 py-16 flex-1 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
        <div className="h-32 bg-gray-100 rounded-2xl mb-6"></div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="h-64 bg-gray-200 rounded-2xl"></div>
          <div className="h-64 bg-gray-100 rounded-2xl lg:col-span-2"></div>
        </div>
      </main>
    );
  }

  return (
    <>
      {/* Breadcrumbs */}
      <div className="max-w-6xl mx-auto w-full px-8 py-4 text-sm text-gray-400 font-medium">
        <Link href="/" className="hover:text-[#0053D6] transition-colors">
          Inicio
        </Link>
        <span className="mx-2">/</span>
        <Link href="/indicadores" className="hover:text-[#0053D6] transition-colors">
          Indicadores
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-600">{displayTitle}</span>
      </div>

      <main className="max-w-6xl mx-auto w-full px-8 pb-16 flex-1 flex flex-col gap-6">
        {/* Header Card del Indicador */}
        <div className="bg-[#F0F5FA] rounded-2xl p-8 border border-blue-50">
          <div className="flex flex-wrap items-center gap-4 mb-3">
            <h2 className="text-3xl md:text-4xl font-[family-name:var(--font-righteous)] text-[#003876] tracking-wide">
              {displayTitle}
            </h2>
            <span className="bg-[#D1E1FF] text-[#0053D6] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
              {hasHistData ? 'Publicado' : 'Vigente'}
            </span>
          </div>
          <div className="text-sm text-[#0053D6]/80 font-medium flex flex-wrap gap-2">
            <span>Tema: {displayCategory}</span>
            <span>·</span>
            <span>Período vigente: {displayLatestPeriod}</span>
            {indicatorMeta?.codigo_interno && (
              <>
                <span>·</span>
                <span>Código: {indicatorMeta.codigo_interno}</span>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Valor Vigente (Izquierda) */}
          <div className="bg-[#003876] text-white rounded-2xl p-8 flex flex-col justify-center shadow-lg lg:col-span-1">
            <h3 className="text-sm font-medium text-blue-200 mb-2">Valor Vigente</h3>
            <div className="text-5xl md:text-6xl font-[family-name:var(--font-righteous)] mb-2 break-words">
              {displayLatestValue}
            </div>
            <div className="text-blue-100/70 text-sm">
              {displayUnit} · Período {displayLatestPeriod}
            </div>
          </div>

          {/* Ficha Metodológica (Derecha) */}
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 lg:col-span-2">
            <h3 className="text-xl font-bold text-gray-800 mb-6">Ficha Metodológica</h3>

            <div className="space-y-4">
              <div className="border-b border-gray-100 pb-4">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">
                  Descripción / Fórmula Descriptiva
                </p>
                <p className="text-sm text-gray-700">{displayDescription}</p>
              </div>
              <div className="border-b border-gray-100 pb-4">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">
                  Expresión de Cálculo
                </p>
                <p className="text-sm text-gray-700 font-mono text-xs bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-100/80 inline-block">
                  {displayFormula}
                </p>
              </div>
              <div className="border-b border-gray-100 pb-4">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">
                  Unidad de Medida
                </p>
                <p className="text-sm text-gray-700">
                  {displayUnit} {displaySymbol ? `(${displaySymbol})` : ''}
                </p>
              </div>
              <div className="border-b border-gray-100 pb-4">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">
                  Periodicidad
                </p>
                <p className="text-sm text-gray-700">{displayPeriodicidad}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Serie Histórica */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 mt-2">
          <h3 className="text-xl font-bold text-gray-800 mb-10">Serie Histórica</h3>

          {hasHistData ? (
            <div className="h-64 w-full flex items-end justify-center sm:justify-start gap-8 sm:gap-16 px-4 mb-4 overflow-x-auto">
              {chartData.map((data, idx) => (
                <div key={idx} className="flex flex-col items-center group w-16 sm:w-24 shrink-0">
                  <span className="text-xs sm:text-sm font-bold text-[#003876] mb-2">
                    {data.label}
                  </span>
                  <div
                    className={`w-full rounded-t-lg transition-all duration-500 ease-in-out ${data.opacity} hover:opacity-80`}
                    style={{ height: `${Math.max((data.value / maxValue) * 100, 6)}%` }}
                  ></div>
                  <div className="w-full border-t-2 border-gray-200 pt-3 mt-0 text-center">
                    <span className="text-sm font-bold text-gray-400">{data.year}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-12 text-center text-sm text-gray-400 bg-gray-50 rounded-xl border border-dashed border-gray-200">
              No hay resultados históricos publicados para este indicador aún.
            </div>
          )}

          <div className="mt-8 border-t border-gray-100 pt-4">
            <p className="text-xs text-gray-400">
              * Valores de corridas e importaciones aprobadas y publicadas oficialmente en Luján.
            </p>
          </div>
        </div>

        {/* Action Buttons - Oculto temporalmente hasta consolidar la información
        <div className="flex flex-col sm:flex-row items-center gap-4 mt-4 border-b border-gray-200 pb-12">
          <a
            href={`/api/externo/resultados-publicos?codigo_interno=${encodeURIComponent(rawId)}&format=csv`}
            download={`indicador-${rawId}.csv`}
            className="bg-[#0052A3] hover:bg-[#003876] text-white font-bold py-3 px-8 rounded-lg shadow transition-colors inline-block text-center"
          >
            Descargar CSV
          </a>
          <button
            onClick={() => window.print()}
            className="bg-white hover:bg-gray-50 text-[#0052A3] font-bold py-3 px-8 rounded-lg shadow-sm border border-gray-200 transition-colors"
          >
            Descargar PDF
          </button>
        </div>
        */}
      </main>
    </>
  );
}
